import EmptyState from '@/components/EmptyState';
import PurgeAccessGate from '@/features/purge/components/PurgeAccessGate';
import PurgeRunDetailPopup from '@/features/purge/components/PurgeRunDetailPopup';
import { usePurgeRuns } from '@/features/purge/hooks/usePurge';
import { RUN_STATUS_COLOR, newestFirst } from '@/features/purge/purge-run-summary';
import { useT } from '@/i18n';
import { formatDateTime } from '@/utils/datetime';
import type { PurgeRun } from '@restaurant/api-contracts';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { Block, BlockFooter, List, ListItem, Navbar, Page, Preloader } from 'framework7-react';
import { useState } from 'react';

function PurgeRunHistoryPage() {
  const t = useT().purge;

  return (
    <PurgeAccessGate title={t.history.pageTitle}>
      <PurgeRunHistoryContent />
    </PurgeAccessGate>
  );
}

function PurgeRunHistoryContent() {
  const t = useT().purge;
  const h = t.history;
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: runs, isLoading, error } = usePurgeRuns();
  const [openRun, setOpenRun] = useState<PurgeRun | null>(null);

  function line(run: PurgeRun): string {
    const what =
      run.target === 'ORDER' ? h.orders(run.affectedCount) : h.reservations(run.affectedCount);
    return h.hidSummary(what);
  }

  function subtitle(run: PurgeRun): string {
    const name = run.criteriaSnapshot.ruleName;
    return name ? `${h.sourceLabel[run.source]} · ${name}` : h.sourceLabel[run.source];
  }

  return (
    <Page name="purge-run-history">
      <Navbar title={h.pageTitle} backLink={settingsBackLink} />

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title={h.couldntLoad}
          description={error instanceof Error ? error.message : undefined}
        />
      )}

      {runs && runs.length === 0 && (
        <EmptyState icon="clock" iconColor="#8e8e93" title={h.empty} description={h.emptyWhy} />
      )}

      {runs && runs.length > 0 && (
        <>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd mediaList>
            {newestFirst(runs).map((run) => (
              <ListItem
                key={run.id}
                link="#"
                onClick={() => setOpenRun(run)}
                title={line(run)}
                subtitle={subtitle(run)}
                text={formatDateTime(run.startedAt)}
                after={run.status === 'COMPLETED' ? undefined : h.statusLabel[run.status]}
              >
                {run.status !== 'COMPLETED' && (
                  <span
                    slot="after-title"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      display: 'inline-block',
                      background: RUN_STATUS_COLOR[run.status],
                    }}
                  />
                )}
              </ListItem>
            ))}
          </List>
          <BlockFooter>{h.footer}</BlockFooter>
        </>
      )}

      <PurgeRunDetailPopup run={openRun} onClose={() => setOpenRun(null)} />
    </Page>
  );
}

export default PurgeRunHistoryPage;
